// React-Wrapper um den framework-agnostischen Toast UI Editor.
//
// Wir benutzen bewusst nicht @toast-ui/react-editor, weil dieser Wrapper
// als peer-dependency React 17 verlangt und seit 2022 nicht mehr gepflegt
// wird. Die Vanilla-Bibliothek @toast-ui/editor ist von React entkoppelt
// und harmoniert auch mit React 18+.
//
// Verhalten:
//  - WYSIWYG ist der Default-Modus, Toolbar erlaubt Wechsel auf Markdown-Source
//  - onChange feuert mit dem aktuellen Markdown-String
//  - externe value-Updates werden in den Editor synchronisiert, ohne den
//    Cursor neu zu setzen, solange der Wert sich nicht unterscheidet

import { useEffect, useRef } from 'react';
import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';

export default function MarkdownEditor({ value, onChange, height = '480px' }) {
    const containerRef = useRef(null);
    const editorRef = useRef(null);
    const onChangeRef = useRef(onChange);

    // onChange stets aktuell halten, ohne den Editor neu zu mounten.
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Initial-Mount des Editors. Läuft nur einmal pro Komponente.
    useEffect(() => {
        const editor = new Editor({
            el: containerRef.current,
            height,
            initialEditType: 'wysiwyg',
            previewStyle: 'tab',
            usageStatistics: false,
            initialValue: value || '',
            autofocus: false,
            toolbarItems: [
                ['heading', 'bold', 'italic', 'strike'],
                ['hr', 'quote'],
                ['ul', 'ol', 'task'],
                ['table', 'link'],
                ['code', 'codeblock'],
            ],
            events: {
                change: () => {
                    if (onChangeRef.current) {
                        onChangeRef.current(editor.getMarkdown());
                    }
                },
            },
        });
        editorRef.current = editor;

        return () => {
            try {
                editor.destroy();
            } catch (_err) {
                // ignore – kann beim StrictMode-Doppel-Mount auftreten
            }
            editorRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Externe value-Änderungen einsynchronisieren (z. B. nach Reload).
    useEffect(() => {
        const ed = editorRef.current;
        if (!ed) return;
        const current = ed.getMarkdown();
        const incoming = value || '';
        if (current !== incoming) {
            ed.setMarkdown(incoming);
        }
    }, [value]);

    return <div ref={containerRef} />;
}
